import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import Emitter from 'quill/core/emitter';
import { ProjectService } from '../../services/project.service';
import { Subject } from 'rxjs';
import { AnnotationPrivateCondition } from '../../interfaces';
import Quill from 'quill';
import { Mention, MentionBlot } from 'quill-mention';
import { UploadFileComponent } from '../mtm-upload-file/upload-file.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EmitterService } from '../../services/emitter.service';
import { CloseAllDropdownsEvent, CommentEditFocusEvent, CommentReplyFocusEvent } from '../../models';
import { take, takeUntil } from 'rxjs/operators';
import { AnnotationService } from '../../services/annotation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { StringUtilsService } from '../../services/string-utils.service';
import { GeneralUtilsService } from '../../services/general-utils.service';

export type QuillSubmitParams = {
  message: string;
  visibility: AnnotationPrivateCondition;
};

Quill.register(
  {
    'blots/mention': MentionBlot,
    'modules/mention': Mention,
  },
  true
);

@Component({
  selector: 'mtm-comment-editor',
  templateUrl: './mtm-comment-editor.component.html',
  styleUrl: './mtm-comment-editor.component.scss',
  standalone: false,
})
export class MtmCommentEditorComponent implements OnInit, OnDestroy {
  @ViewChild('container', { static: false }) container: ElementRef;
  @ViewChild('mtmUpload', { static: false }) mtmUpload: UploadFileComponent;

  private readonly projectService = inject(ProjectService);
  private readonly modalService = inject(NgbModal);
  private readonly annotationService = inject(AnnotationService);
  private readonly stringUtilsService = inject(StringUtilsService);

  @Input() relatedCommentId: string = '';
  @Input() elementId: string = '';
  @Input() placeholder?: string = '';
  @Input() hideQuillToolbar: boolean = false;
  @Input() files: any = [];
  @Input() videoRecordFiles: any = [];
  @Input() audioRecordFiles: any = [];
  @Input() visibilitySelectorEnabled: boolean = true;
  @Input() filesToolbarVisible: boolean = true;
  @Input() cancelButtonId: string = 'btnCancelComment';
  @Input() cancelButtonEnabled: boolean = false;
  @Input() saveButtonEnabled: boolean = false;
  @Input() editButtonEnabled: boolean = false;
  @Input() instanceId: string;
  @Output() enterKeydownPress: EventEmitter<any> = new EventEmitter<any>();
  @Output() enterKeyPress: EventEmitter<any> = new EventEmitter<any>();
  @Output() fileUpload: EventEmitter<any> = new EventEmitter();
  @Output() fileDelete: EventEmitter<any> = new EventEmitter();
  @Output() cancelClick: EventEmitter<any> = new EventEmitter();
  @Output() submit: EventEmitter<QuillSubmitParams> = new EventEmitter<QuillSubmitParams>();
  @Output() editorClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() editClick: EventEmitter<any> = new EventEmitter<any>();

  modelValue: string;
  _commentVisibility: AnnotationPrivateCondition = null;

  @Input()
  get value() {
    return this.modelValue;
  }

  @Output() valueChange = new EventEmitter<string>();

  set value(val: string) {
    this.modelValue = val;
    this.valueChange.emit(this.modelValue);
  }

  @Input()
  get commentVisibility() {
    return this._commentVisibility;
  }

  @Output() commentVisibilityChange = new EventEmitter<AnnotationPrivateCondition>();
  set commentVisibility(val: AnnotationPrivateCondition) {
    this._commentVisibility = val;
    console.log('commentVisibility', val);
    this.commentVisibilityChange.emit(val);
  }

  attachmentsValue: any[];

  @Input()
  get attachments() {
    return this.attachmentsValue;
  }

  @Output() attachmentsChange = new EventEmitter();

  set attachments(val) {
    this.attachmentsValue = val;
    this.attachmentsChange.emit(this.attachmentsValue);
  }

  _backgroundColor: string = '';

  @Input()
  get backgroundColor(): string {
    return this._backgroundColor;
  }

  @Output() backgroundColorChange = new EventEmitter<string>();
  set backgroundColor(value: string) {
    this._backgroundColor = value;
    this.backgroundColorChange.emit(value);
    this.container.nativeElement.style.backgroundColor = value;
  }

  toolbarHidden: boolean = false;
  quillModule: any = null;
  readonly quillCustomModules = [
    { path: 'blots/mention', implementation: MentionBlot },
    { path: 'modules/mention', implementation: Mention },
  ];
  quillInstance: any = null;
  ngUnsubscribe = new Subject<void>();

  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly generalUtilsService: GeneralUtilsService
  ) {}

  ngOnInit() {
    this.initQuill();

    EmitterService.get(CommentReplyFocusEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: any) => {
        const { parentId: parentCommentId } = data;
        if (parentCommentId === this.relatedCommentId) {
          setTimeout(() => {
            this.quillInstance?.focus();
          }, 10);
        }
      });
    EmitterService.get(CommentEditFocusEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: any) => {
        const { commentId } = data;
        if (commentId === this.relatedCommentId) {
          setTimeout(() => {
            this.quillInstance?.focus();
          }, 10);
        }
      });

    if (this.visibilitySelectorEnabled) {
      this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
        this.commentVisibility = value;
      });
    }

    EmitterService.get(CloseAllDropdownsEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.quillInstance?.getModule('mention')?.hideMentionList?.();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initQuill() {
    const __this = this;
    this.quillModule = {
      toolbar: this.hideQuillToolbar
        ? false
        : {
            container: [
              ['bold', 'italic', 'underline'],
              //['bold', 'italic', 'underline', 'link', 'emoji'],
            ],
            handlers: {
              link: (value) => {
                if (value) {
                  const editorInstance = this.quillInstance;
                  const range = editorInstance.selection;
                  if (range.savedRange == null || range.savedRange.length === 0) return;
                  const preview = __this.getSelectedText();
                  const { tooltip } = editorInstance.theme;
                  tooltip.save = function () {
                    let val = tooltip.textbox.value;
                    if (val.indexOf('https') === -1 && val.indexOf('http') === -1) {
                      val = 'https://' + val;
                    }
                    this.quill.format('link', val, Emitter.sources.USER);
                  };
                  tooltip.edit('link', preview);
                }
              },
            },
          },
      // 'emoji-toolbar': true,
      // keyboard: {
      //   bindings: {
      //     linebreak: {
      //       key: 13,
      //       shiftKey: false,
      //       handler: function () {
      //         __this.enterKeyPress.emit();
      //       }
      //     }
      //   }
      // },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        positioningStrategy: 'fixed',
        mentionContainerClass: 'comment-editor-mention-container ql-mention-list-container',
        source: (searchTerm, renderList, mentionChar) => {
          const values = __this.projectService.project.participants
            .map((participant) => ({
              id: participant.username,
              value: participant.fullName,
              type: 'one_on_one',
              denotationChar: mentionChar,
            }))
            .filter((data) => !this.generalUtilsService.isObjectEmpty(data.value.trim()));
          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches = [];
            for (let i = 0; i < values.length; i++)
              if (
                ~this.stringUtilsService
                  .getNormalizedName(values[i].value)
                  .toLowerCase()
                  .indexOf(this.stringUtilsService.getNormalizedName(searchTerm).toLowerCase())
              )
                matches.push(values[i]);
            renderList(matches, searchTerm);
          }
        },
      },
    };
  }

  private setQuillTitleAttributes() {
    const toolbar = this.quillInstance.getModule('toolbar');
    const buttonTitles = {
      '.ql-bold': 'textEditorBoldText',
      '.ql-italic': 'textEditorItalicText',
      '.ql-underline': 'textEditorUnderlineText',
      '.ql-link': 'textEditorInsertLink',
      '.ql-emoji': 'textEditorInsertEmoji',
    };

    Object.keys(buttonTitles).forEach((key) => {
      const button = toolbar.container.querySelector(key);
      if (button) {
        button.setAttribute('title', this.translatePipe.transform(buttonTitles[key]));
      }
    });
  }

  private getSelectedText() {
    let txt;
    if (window.getSelection) {
      txt = window.getSelection();
    } else if (window.document.getSelection) {
      txt = window.document.getSelection();
    } else if ((window.document as any).selection) {
      txt = (window.document as any).selection.createRange().text;
    }
    return txt;
  }

  toggleToolbar() {
    this.toolbarHidden = !this.toolbarHidden;
  }

  cancel() {
    console.log('cancel clicked');
    this.cancelClick.emit({
      instanceId: this.instanceId,
    });
  }

  send() {
    this.submit.emit({
      message: this.value,
      visibility: null,
    });
  }

  onEditorInitialized(editor: any) {
    this.quillInstance = editor;
    this.setQuillTitleAttributes();
  }

  toggleEmojiPicker() {
    const toolbar = this.quillInstance.getModule('toolbar');
    const emojiButton = toolbar.container.querySelector('.ql-emoji');

    if (emojiButton) {
      emojiButton.click();
    }
    this.triggerEditorClick();
  }

  openFileExplorer() {
    if (!this.mtmUpload) {
      console.log('mtmUpload not found');
      return;
    }

    this.triggerEditorClick();
    this.mtmUpload.fileInput.nativeElement.click();
  }

  deleteFile(event, file) {
    this.fileDelete.emit(file);
  }

  handleFileUpload(params: { files: any[] }) {
    this.fileUpload.emit(params);
  }

  textChanged($event) {
    if ($event.editor.getLength() > 1024) {
      $event.editor.deleteText(1024, $event.editor.getLength());
    }
  }

  setVisibility(value: AnnotationPrivateCondition) {
    this.commentVisibility = value;
    this.annotationService.updatePrivateCondition(value);
  }

  triggerEditorClick() {
    this.editorClick.emit();
  }

  edit() {
    this.editClick.emit();
  }
}
